import {
  Component,
  OnDestroy,
  AfterViewInit,
  Input,
  forwardRef
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

import { TinyMceHelper, TinyMceEditor, TinyMce } from './tiny-mce-helper'
import { UploadService } from '../../../../data/services'
import { Upload } from '../../../../data/models'

const DEFAULT_TEXTBOX_VALUE = Object.freeze('')

/*
 * Plugins: To be loaded beforehand in tiny-mce.module.ts
 * Skin: To be copied from 'node_modules/tinymce/' to client application
 */
const TINY_MCE_CONFIG = Object.freeze({
  PLUGINS: ['link', 'paste', 'table', 'formula', 'image'],
  SKIN_URL: 'assets/vendor/tinymce/skins/lightgray',
  TOOLBAR: `
    styleselect |
    bold italic |
    link image |
    formula |
    alignleft aligncenter alignright alignjustify |
    bullist numlist outdent indent`
})

/**
 * @see https://www.tinymce.com/docs/integrations/angular2/
 * @see https://coryrylan.com/blog/angular-custom-form-controls-with-reactive-forms-and-ngmodel
 * @see https://www.tinymce.com/docs/api/tinymce/tinymce.editor/
 * @see https://www.tinymce.com/docs/advanced/events/
 */
@Component({
  selector: 'mp-tiny-mce',
  template: `<textarea [id]="elementId"></textarea>`,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TinyMceComponent),
      multi: true
    }
  ]
})
export class TinyMceComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  public elementId: string

  @Input() public height: number
  @Input() public readOnly = false
  @Input() public menubar: string
  @Input() public toolbar: string
  @Input() public placeholderText: string = ''

  private _onChange: any
  private _onTouched: any

  private _editor: TinyMceEditor
  private _mockEditor
  private _tinymce: TinyMce

  public constructor(
    tinymce: TinyMceHelper,
    private _uploadService: UploadService
  ) {
    this._tinymce = tinymce.instance
    this._onTouched = this._onChange = () => { }
    this._mockEditor = new MockEditor()
    this._editor = this._mockEditor
    this.elementId = this._generateRandomElementId()
  }

  public get value(): string {
    return this._editor.getContent()
  }

  public set value(val: string) {
    // listeners will potentially fire here when the content is set
    // however, on change, we're also calling this function
    // therefore we need to ensure, the value differs before setting endlessly
    if (this.value !== val) {
      this._editor.setContent(val)
      this._onValueChanged(val)
    }
  }

  public writeValue(value: any) {
    if (typeof value === 'string') {
      this.value = value
    }
  }

  public registerOnChange(fn: (val: string) => void) {
    this._onChange = fn
  }

  public registerOnTouched(fn: () => void) {
    this._onTouched = fn
  }

  public ngAfterViewInit() {
    const toolbar = this.toolbar !== undefined ? this.toolbar : TINY_MCE_CONFIG.TOOLBAR
    const tinymceOptions = {
      selector: `#${this.elementId}`,
      plugins: TINY_MCE_CONFIG.PLUGINS,
      skin_url: TINY_MCE_CONFIG.SKIN_URL,
      height: this.height,
      readonly: this.readOnly,
      statusbar: !this.readOnly,
      toolbar: this.readOnly ? false : toolbar,
      placeholder: this.placeholderText,
      setup: (editor: TinyMceEditor) => {
        this._editor = editor
        if (this.placeholderText !== '') {
          this._setupPlaceholderText.bind(this)(this._editor, this.placeholderText)
        }
      },
      formula: {
        path: 'assets/vendor/tinymce/plugins/formula'
      },
      images_upload_handler: this._fileUploadHandler.bind(this),
      images_dataimg_filter: (img: HTMLImageElement) => false
    }

    if (this.menubar !== undefined) {
      tinymceOptions['menubar'] = this.menubar
    }

    if (this.readOnly) {
      tinymceOptions['menubar'] = !this.readOnly
    }

    this._tinymce.init(tinymceOptions)
    this._setEditorAfterInit()
  }

  public ngOnDestroy() {
    this._tinymce.remove(this._editor)
  }

  private _setupPlaceholderText(editor: TinyMceEditor, placeholderText: string) {
    editor.on('init', function() {
      const editorPlaceholderText = `<p style="color: gray;">${placeholderText}</p>`
      // Check if the editor content is empty or only contains the placeholder text
      if (editor.getContent() === '') {
        // Set the placeholder text as the initial content
        editor.setContent(editorPlaceholderText)
      }

      // Listen for focus events to remove the placeholder text
      editor.on('focus', function() {
        const content = editor.getContent()
        if (content == editorPlaceholderText) {
          // Clear the editor content when it gains focus
          editor.setContent('')
          editor.execCommand('RemoveFormat')
        }
      })

      // Listen for blur events to add the placeholder text back if the content is empty
      editor.on('blur', function() {
        const content = editor.getContent()
        if (content === '') {
          // Restore the placeholder text when the editor loses focus
          editor.setContent(editorPlaceholderText)
        }
      })
    })
  }

  private _onValueChanged(val: string) {
    this._onChange(val)
    this._onTouched()
  }

  private _setEditorAfterInit() {
    this._editor.setContent(this._mockEditor.getContent())
    this._editor.on('Change', () => this._onValueChanged(this._editor.getContent()))
    this._editor.on('keyup', () => this._onValueChanged(this._editor.getContent()))
    this._editor.on('Dirty', () => this._onValueChanged(this._editor.getContent()))
    this._editor.on('NodeChange', () => this._onTouched())
  }

  private _generateRandomElementId(): string {
    const randomString = Math.random().toString(36).substring(2, 5)
    return `${TinyMceComponent.name}-${randomString}`
  }

  private _fileUploadHandler(blobInfo, success, failure) {
    const upload = new Upload()
    upload.locationUrl = blobInfo.filename()
    upload.category = 'exams'
    upload.content = blobInfo.base64()
    this._uploadService.create(upload).subscribe((serverUpload) => {
      success(serverUpload.locationUrl)
    })
  }

}

class MockEditor {
  private _value = DEFAULT_TEXTBOX_VALUE

  public getContent(): string {
    return this._value
  }

  public setContent(value) {
    this._value = value
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/angular/components/form/tiny-mce/tiny-mce.component.ts